import React, { useEffect, useId } from 'react';

import { Alert, Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, SxProps, Theme } from '@mui/material';

import { REFUND_METHOD_CREDITS, REFUND_METHOD_DEFAULT } from '~/consts/refund';

import { RefundMethods } from './RefundV2/Utils/RefundUtils';
import getRefundOptions from './RefundV2/Utils/getRefundOptions';

interface Props {
  order: App.Order;
  refundMethods: ReadonlyArray<RefundMethods>;
  name?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: RefundMethods) => void;
  label?: React.ReactNode;
  required?: boolean;
  size?: 'small' | 'medium';
  optionLabels?: Partial<Record<RefundMethods, string>>;
  sx?: SxProps<Theme>;
}

/** Shared refund policy, selection handling and presentation for all refund flows. */
export default function RefundMethodField({
  order,
  refundMethods,
  name = 'refundMethod',
  value,
  defaultValue,
  onChange,
  label = 'Method of refund to customer',
  required = true,
  size = 'medium',
  optionLabels,
  sx,
}: Props) {
  const labelId = useId();
  const { options, creditsHidden, notice } = getRefundOptions(refundMethods, order);
  const hasHiddenCreditSelection = creditsHidden && value === REFUND_METHOD_CREDITS;
  const permittedDefault = creditsHidden && defaultValue === REFUND_METHOD_CREDITS ? undefined : defaultValue;

  // Controlled legacy submitters read parent state, not FormData. Keep it in sync
  // with the visible fallback when a policy-provided Credits selection is hidden.
  useEffect(() => {
    if (hasHiddenCreditSelection) {
      onChange?.(REFUND_METHOD_DEFAULT);
    }
  }, [hasHiddenCreditSelection, onChange]);

  return (
    <Box sx={sx}>
      <FormControl required={required}>
        {label && (
          <FormLabel id={labelId} sx={{ fontWeight: 'bold' }}>
            {label}
          </FormLabel>
        )}
        <RadioGroup
          name={name}
          aria-labelledby={label ? labelId : undefined}
          aria-label={label ? undefined : 'Method of refund to customer'}
          row
          value={hasHiddenCreditSelection ? REFUND_METHOD_DEFAULT : value}
          defaultValue={permittedDefault}
          onChange={(_, nextValue) => onChange?.(nextValue as RefundMethods)}
        >
          {options.map(({ value, label }) => {
            return (
              <FormControlLabel
                value={value}
                data-testid={`refundMethod-${value}`}
                data-cy={`refundMethod-${value}`}
                label={optionLabels?.[value] ?? label}
                control={
                  <Radio
                    size={size}
                    // don't set required on the `Radio` as it will show asterisk on all of the inputs
                    inputProps={{
                      required,
                    }}
                  />
                }
                key={value}
              />
            );
          })}
        </RadioGroup>
      </FormControl>
      {notice && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {notice}
        </Alert>
      )}
    </Box>
  );
}
